import React from "react";
import { XCircle, Activity } from "lucide-react";
import { pingColor, pingLabel, pingBars } from "../lib/format";
import { useLatency } from "../context/LatencyContext";

export const PingIndicator = ({ ping, online = true, server = null, showLabel = false, testId }) => {
  const { enabled: probeEnabled, baseline, adjustedPing, computeHomePing } = useLatency();
  if (!online) {
    return (
      <span
        className="inline-flex items-center gap-1.5 font-mono-hud px-2 py-0.5 border tracking-wider"
        style={{
          color: "var(--ping-high)",
          borderColor: "color-mix(in srgb, var(--ping-high) 55%, transparent)",
          background: "color-mix(in srgb, var(--ping-high) 10%, transparent)",
          borderRadius: 2,
          fontSize: "0.72rem",
        }}
        data-testid={testId}
      >
        <XCircle className="w-3.5 h-3.5" />
        OFFLINE
      </span>
    );
  }
  const useProbe = probeEnabled && baseline != null && server;
  const displayPing = useProbe ? adjustedPing(server) : ping;
  const serverPing = ping;
  // Always compute the "from home" value from the baseline, independent of
  // whether HOME mode is active, so the tooltip reflects reality in both modes.
  const homePing = computeHomePing ? computeHomePing(server) : null;
  const color = pingColor(displayPing);
  const bars = pingBars(displayPing);
  // Rich tooltip: show BOTH values so users can see the difference between
  // the server-reported regional estimate and their own browser-measured RTT.
  const tooltip = (() => {
    const parts = [];
    parts.push(`Server estimate: ${serverPing}ms`);
    if (homePing != null) parts.push(`From home: ${homePing}ms`);
    if (baseline != null) parts.push(`Browser baseline: ${baseline}ms`);
    parts.push(useProbe ? "Showing home-measured ping" : "Showing server estimate");
    return parts.join(" · ");
  })();
  return (
    <span
      className="inline-flex items-center gap-2 font-mono-hud"
      style={{ color }}
      data-testid={testId}
      title={tooltip}
    >
      <span className="ping-bars" aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={i <= bars ? "on" : ""} style={{ height: `${i * 3 + 2}px` }} />
        ))}
      </span>
      <span className="tabular-nums" style={{ minWidth: 44 }}>
        {displayPing}ms
      </span>
      {useProbe && (
        <Activity className="w-2.5 h-2.5" style={{ color: "var(--primary-accent)", opacity: 0.7 }} />
      )}
      {showLabel && <span className="hud-label" style={{ color }}>{pingLabel(displayPing)}</span>}
    </span>
  );
};

export default PingIndicator;
